/**
 * The app has to be able to survive its own boot (BUG-120).
 *
 * On 2026-08-05 a boot-path task read nine stuck videos into memory before
 * first paint. iOS killed the renderer, Safari relaunched, the same task ran
 * again, and it was killed again — forever. Nothing in the app noticed it was
 * in a loop, so the only way out was deleting the app and everything in it,
 * including six recordings that had never been sent.
 *
 * The root cause is fixed. This is the net under it: boot counts its own
 * attempts, and after two that never finished it opens in RECOVERY MODE —
 * every optional launch task skipped, so the app comes up even if one of them
 * is what kills it. A launch that stays alive resets the count, so the net is
 * invisible on every ordinary day and self-clears the moment things are well.
 *
 * Deliberately localStorage and deliberately synchronous: whatever records a
 * crash has to be written before the thing that might crash, and IndexedDB is
 * neither. A device that refuses storage simply never gets the net.
 */

const KEY = 'momento.bootAttempts';

/** Two failed launches is a pattern; one is a bad day. */
const RECOVERY_AFTER = 2;

/**
 * How long a launch must survive to count as healthy.
 *
 * Long enough to be past the boot work (queue drain, upload resume, prefetch),
 * short enough that a real user is still holding the phone. The crash this
 * guards against happens within the first second or two.
 */
const HEALTHY_AFTER_MS = 8000;

function read(): number {
  try {
    const raw = Number(localStorage.getItem(KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  } catch {
    return 0;
  }
}

let recovery = false;

/**
 * Call once, as early as possible, BEFORE any work that could kill the tab.
 * Returns whether this launch is a recovery launch.
 */
export function markBootStart(): boolean {
  const attempts = read();
  recovery = attempts >= RECOVERY_AFTER;
  try {
    localStorage.setItem(KEY, String(attempts + 1));
  } catch {
    /* no storage, no net — the app still boots */
  }
  return recovery;
}

/**
 * This launch is optional-work-free because the last two died trying.
 *
 * Read by boot tasks, not by render: recovery mode must never change what the
 * app LOOKS like, only how much it tries to do the moment it opens.
 */
export function inRecoveryMode(): boolean {
  return recovery;
}

/** The launch survived — forget the attempts so the next one is normal. */
export function markBootHealthy(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to forget */
  }
}

/** Start the clock that clears the counter if this launch lives long enough. */
export function watchForHealthyBoot(): void {
  setTimeout(markBootHealthy, HEALTHY_AFTER_MS);
}
